import React, {
    useState,
    useEffect
} from "react";

import {
    Link,
    useLocation,
    useNavigate
} from "react-router-dom";

import axios from "axios";

import {
    LayoutDashboard,
    Map,
    Trophy,
    Shield,
    BookOpen,
    Upload,
    User,
    LogOut,
    BarChart2,
    PlayCircle,
    X,
    Eye,
    Palette,
    CircleDot,
    Zap,
    Rocket,
    Brain,
    Search,
    Target,
    FileText,
    Menu
} from "lucide-react";

import "./IgniteNavbar.css";


const IgniteNavbar = () => {

    const location = useLocation();

    const navigate = useNavigate();


    /* =====================================================
       STATES
    ===================================================== */

    const [
        showTraining,
        setShowTraining
    ] = useState(false);


    const [
        mobileMenuOpen,
        setMobileMenuOpen
    ] = useState(false);


    const [
        profilePhoto,
        setProfilePhoto
    ] = useState(
        localStorage.getItem(
            "profile_photo"
        ) || ""
    );


    const [
        level,
        setLevel
    ] = useState(
        Number(
            localStorage.getItem(
                "user_level"
            )
        ) || 1
    );


    /* =====================================================
       EMAIL
    ===================================================== */

    const email =
        localStorage.getItem(
            "user_email"
        );


    /* =====================================================
       LEVEL NAMES
    ===================================================== */

    const levelName = {

        1: "AWARE",
        2: "EXPLORER",
        3: "ADAPTER",
        4: "THRIVER"

    };


    /* =====================================================
       LOAD USER DATA
    ===================================================== */

    const loadUserData = async () => {

        if (!email) {
            return;
        }

        try {

            const response =
                await axios.get(
                    `/api/ignite/profile/${encodeURIComponent(
                        email
                    )}`
                );


            const user =
                response.data?.user ||
                response.data?.profile ||
                response.data;


            /* =============================================
               PROFILE PHOTO
            ============================================= */

            const photo =
                user?.profilePhoto ||
                response.data?.profilePhoto ||
                "";


            if (photo) {

                setProfilePhoto(photo);

                localStorage.setItem(
                    "profile_photo",
                    photo
                );

            }


            /* =============================================
               LEVEL
            ============================================= */

            const backendLevel =
                Number(
                    user?.level ||
                    response.data?.level ||
                    response.data?.currentLevel ||
                    1
                );


            setLevel(
                backendLevel
            );


            localStorage.setItem(
                "user_level",
                String(
                    backendLevel
                )
            );

        }

        catch (error) {

            console.error(
                "Ignite Navbar Profile Error:",
                error
            );

        }

    };


    /* =====================================================
       LOAD ON PAGE OPEN
    ===================================================== */

    useEffect(() => {

        loadUserData();

    }, [email]);


    /* =====================================================
       PROFILE UPDATE EVENT
    ===================================================== */

    useEffect(() => {

        const handleProfileUpdate = (
            event
        ) => {

            const newPhoto =
                event.detail?.photo;


            if (newPhoto) {

                setProfilePhoto(
                    newPhoto
                );

                localStorage.setItem(
                    "profile_photo",
                    newPhoto
                );

            }


            const newLevel =
                event.detail?.level;


            if (
                newLevel !== undefined &&
                newLevel !== null
            ) {

                const numericLevel =
                    Number(
                        newLevel
                    );


                if (
                    numericLevel >= 1 &&
                    numericLevel <= 4
                ) {

                    setLevel(
                        numericLevel
                    );

                    localStorage.setItem(
                        "user_level",
                        String(
                            numericLevel
                        )
                    );

                }

            }

        };


        window.addEventListener(
            "profileUpdated",
            handleProfileUpdate
        );


        return () => {

            window.removeEventListener(
                "profileUpdated",
                handleProfileUpdate
            );

        };

    }, []);


    /* =====================================================
       REFRESH WHEN WINDOW FOCUS
    ===================================================== */

    useEffect(() => {

        const handleFocus = () => {

            loadUserData();

        };


        window.addEventListener(
            "focus",
            handleFocus
        );


        return () => {

            window.removeEventListener(
                "focus",
                handleFocus
            );

        };

    }, [email]);


    /* =====================================================
       CLOSE MOBILE MENU WHEN ROUTE CHANGES
    ===================================================== */

    useEffect(() => {

        setMobileMenuOpen(false);

    }, [location.pathname]);


    /* =====================================================
       MENU
    ===================================================== */

    const menus = [

        {
            title: "Dashboard",
            icon: <LayoutDashboard size={20} />,
            path: "/ignite/dashboard"
        },

        {
            title: "Journey",
            icon: <Map size={20} />,
            path: "/ignite/journey"
        },

        {
            title: "Performance",
            icon: <BarChart2 size={20} />,
            path: "/ignite/performance"
        },

        {
            title: "Reports",
            icon: <FileText size={20} />,
            path: "/ignite/report"
        },

        {
            title: "Arena",
            icon: <Trophy size={20} />,
            path: "/ignite/arena"
        },

        {
            title: "Vault",
            icon: <Shield size={20} />,
            path: "/ignite/vault"
        },

        {
            title: "Courses",
            icon: <BookOpen size={20} />,
            path: "/ignite/courses"
        },

        {
            title: "Uploads",
            icon: <Upload size={20} />,
            path: "/ignite/upload"
        },

        {
            title: "Profile",
            icon: <User size={20} />,
            path: "/ignite/profile"
        }

    ];


    /* =====================================================
       TRAINING TESTS
    ===================================================== */

    const tests = [

        {
            title: "Visual Acuity",
            icon: <Eye size={22} />,
            path: "/visual-acuity"
        },

        {
            title: "Color Vision",
            icon: <Palette size={22} />,
            path: "/color-vision"
        },

        {
            title: "Contrast Sensitivity",
            icon: <CircleDot size={22} />,
            path: "/contrast"
        },

        {
            title: "Reaction Speed",
            icon: <Zap size={22} />,
            path: "/reactiongame"
        },

        {
            title: "Complex Reaction",
            icon: <Rocket size={22} />,
            path: "/complex-reaction"
        },

        {
            title: "Short Term Memory",
            icon: <Brain size={22} />,
            path: "/short-term-memory"
        },

        {
            title: "Working Memory",
            icon: <Brain size={22} />,
            path: "/working-memory"
        },

        {
            title: "Search Span",
            icon: <Search size={22} />,
            path: "/search-span"
        },

        {
            title: "Anticipation",
            icon: <Target size={22} />,
            path: "/anticipation"
        }

    ];


    /* =====================================================
       OPEN TEST
    ===================================================== */

    const openTest = (path) => {

        setShowTraining(false);

        setMobileMenuOpen(false);

        navigate(path);

    };


    /* =====================================================
       LOGOUT
    ===================================================== */

    const handleLogout = () => {

        localStorage.removeItem(
            "user_email"
        );

        setMobileMenuOpen(false);

        navigate(
            "/dashboard-login"
        );

    };


    /* =====================================================
       TOGGLE MOBILE MENU
    ===================================================== */

    const toggleMobileMenu = () => {

        setMobileMenuOpen(
            previous => !previous
        );

    };


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <>

            {/* =================================================
                MOBILE HAMBURGER
            ================================================= */}

            <button
                className="ignite-mobile-menu-btn"
                type="button"
                onClick={toggleMobileMenu}
                aria-label="Open navigation"
            >

                <Menu size={23} />

            </button>


            {/* =================================================
                MOBILE OVERLAY
            ================================================= */}

            {mobileMenuOpen && (

                <div
                    className="ignite-mobile-overlay"
                    onClick={() =>
                        setMobileMenuOpen(false)
                    }
                />

            )}


            {/* =================================================
                SIDEBAR
            ================================================= */}

            <aside
                className={`ignite-sidebar ${
                    mobileMenuOpen
                        ? "mobile-open"
                        : ""
                }`}
            >


                {/* =================================================
                    MOBILE CLOSE
                ================================================= */}

                <button
                    className="ignite-mobile-close"
                    type="button"
                    onClick={() =>
                        setMobileMenuOpen(false)
                    }
                    aria-label="Close navigation"
                >

                    <X size={22} />

                </button>


                {/* =================================================
                    LOGO
                ================================================= */}

                <a
                    href="https://forgeyourpeak.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ignite-logo-link"
                >

                    <div className="ignite-logo">

                        <div className="forge-logo-icon">

                            <img
                                src="/Logo.png"
                                alt="Forge Your Peak"
                            />

                        </div>

                        <div>

                            <h2>
                                Forge Your Peak
                            </h2>

                            <p>
                                ELITE PERFORMANCE
                            </p>

                        </div>

                    </div>

                </a>


                {/* =================================================
                    NAVIGATION
                ================================================= */}

                <nav>

                    {menus.map(
                        (menu) => (

                            <Link
                                key={menu.title}
                                to={menu.path}
                                className={
                                    location.pathname ===
                                    menu.path
                                        ? "ignite-link active"
                                        : "ignite-link"
                                }
                                onClick={() =>
                                    setMobileMenuOpen(false)
                                }
                            >

                                {menu.icon}

                                <span>
                                    {menu.title}
                                </span>

                            </Link>

                        )
                    )}

                </nav>


                {/* =================================================
                    START TRAINING
                ================================================= */}

                <div className="ignite-training">

                    <button
                        className="training-btn"
                        onClick={() => {

                            setShowTraining(true);

                            setMobileMenuOpen(false);

                        }}
                    >

                        <PlayCircle size={20} />

                        <span>
                            Start Training
                        </span>

                    </button>

                </div>


                {/* =================================================
                    BOTTOM
                ================================================= */}

                <div className="ignite-bottom">

                    <button
                        onClick={handleLogout}
                    >

                        <LogOut size={18} />

                        <span>
                            Logout
                        </span>

                    </button>

                </div>


            </aside>


            {/* =====================================================
                TRAINING MODAL
            ===================================================== */}

            {showTraining && (

                <div
                    className="training-modal-overlay"
                    onClick={() =>
                        setShowTraining(false)
                    }
                >

                    <div
                        className="training-modal"
                        onClick={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="training-modal-header">

                            <div>

                                <span className="training-eyebrow">
                                    IGNITE TRAINING
                                </span>

                                <h2>
                                    Choose Your Assessment
                                </h2>

                                <p>
                                    Select a test to start your performance training.
                                </p>

                            </div>

                            <button
                                className="training-close"
                                onClick={() =>
                                    setShowTraining(false)
                                }
                            >

                                <X size={22} />

                            </button>

                        </div>


                        <div className="training-test-grid">

                            {tests.map(
                                (test) => (

                                    <button
                                        key={test.title}
                                        className="training-test-card"
                                        onClick={() =>
                                            openTest(
                                                test.path
                                            )
                                        }
                                    >

                                        <div className="training-test-icon">

                                            {test.icon}

                                        </div>

                                        <div className="training-test-content">

                                            <h3>
                                                {test.title}
                                            </h3>

                                            <span>
                                                Start assessment →
                                            </span>

                                        </div>

                                    </button>

                                )
                            )}

                        </div>

                    </div>

                </div>

            )}

        </>

    );

};


export default IgniteNavbar;